// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type {
  AutoUpdateOptions,
  DetectOverflowOptions,
  FlipOptions,
  Middleware,
  Placement,
  ReferenceElement,
  ShiftOptions,
  Strategy,
} from '@floating-ui/dom'
import {
  autoUpdate,
  computePosition,
  flip,
  limitShift,
  offset,
  shift,
} from '@floating-ui/dom'
import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Host,
  Listen,
  Method,
  Prop,
  Watch,
} from '@stencil/core'

import type { OffsetOptions } from '../../helpers'
import {
  ignorePromise,
  propertyToPx,
  resolveTarget,
  roundByDPR,
} from '../../helpers'

let autoIncrementingId = 1

const isQdsButton = (
  element: globalThis.Element,
): element is HTMLQdsButtonElement =>
  element.tagName.toLowerCase() === 'qds-button'

/**
 * @slot Content to display in the dropdown.
 * @see https://quartz.se.com/build/components/dropdown
 */
@Component({
  tag: 'qds-dropdown',
  shadow: true,
  styleUrl: 'dropdown.css',
})
export class Dropdown implements ComponentInterface {
  /**
   * Options to pass to Floating UI's
   * [`autoUpdate()` function](https://floating-ui.com/docs/autoUpdate).
   *
   * @see https://floating-ui.com/docs/autoupdate#options
   */
  @Prop() public readonly autoUpdateOptions?: AutoUpdateOptions

  /**
   * Prevents the dropdown from being shown by user interaction. The dropdown
   * can still be displayed by calling the `show()` method.
   */
  @Prop() public readonly disabled: boolean = false

  /**
   * Options to pass to Floating UI's
   * [`flip` middleware](https://floating-ui.com/docs/flip).
   *
   * The [`crossAxis` option](https://floating-ui.com/docs/flip#crossaxis) is
   * set to the value of the `noShift` property by default for interoperability
   * with the [`shift` middleware](https://floating-ui.com/docs/shift).
   *
   * @see https://floating-ui.com/docs/flip#options
   */
  @Prop() public readonly flipOptions?: Partial<
    DetectOverflowOptions & FlipOptions
  >

  /**
   * Disable Floating UI's
   * [`flip` middleware](https://floating-ui.com/docs/flip).
   */
  @Prop() public readonly noFlip: boolean = false

  /**
   * Disable Floating UI's
   * [`shift` middleware](https://floating-ui.com/docs/shift).
   */
  @Prop() public readonly noShift: boolean = false

  /**
   * Options to pass to Floating UI's
   * [`offset` middleware](https://floating-ui.com/docs/offset).
   *
   * The [`mainAxis` option](https://floating-ui.com/docs/offset#mainaxis) is
   * not supported.
   *
   * @see https://floating-ui.com/docs/offset#options
   */
  @Prop() public readonly offsetOptions?: OffsetOptions

  /**
   * Where to place the dropdown relative to the `target`.
   *
   * @see https://floating-ui.com/docs/tutorial#placements
   */
  @Prop() public readonly placement: Placement = 'bottom-start'

  /**
   * Options to pass to Floating UI's
   * [`shift` middleware](https://floating-ui.com/docs/shift).
   *
   * @see https://floating-ui.com/docs/shift#options
   */
  @Prop() public readonly shiftOptions?: Partial<
    DetectOverflowOptions & ShiftOptions
  >

  /**
   * The CSS position strategy to use.
   *
   * @see https://floating-ui.com/docs/computeposition#strategy
   */
  @Prop() public readonly strategy?: Strategy

  /**
   * The trigger element that will trigger the dropdown. The target specified
   * can be any of the following:
   *
   * - A CSS selector string
   * - A reference to an [`Element`][] or [Virtual Element][]
   * - A function returning a reference to an [`Element`][],
   * [Virtual Element][], or a CSS selector string
   *
   * [`Element`]: https://developer.mozilla.org/docs/Web/API/Element
   * [Virtual Element]: https://floating-ui.com/docs/virtual-elements
   */
  @Prop() public readonly target!:
    | ReferenceElement
    | string
    | (() => ReferenceElement | string)

  /**
   * Emitted when the user instructs the browser that they wish to dismiss the
   * current open dropdown by pressing the `Esc` key.
   *
   * When a dropdown is dismissed with the `Esc` key, both the `qdsCancel` and
   * `qdsClose` events are emitted.
   */
  @Event({ eventName: 'qdsCancel', bubbles: false })
  private readonly cancelEmitter!: EventEmitter<void>

  /**
   * Emitted when the dropdown is closed.
   */
  @Event({ eventName: 'qdsClose', bubbles: false, cancelable: false })
  private readonly closeEmitter!: EventEmitter<void>

  @Element() private readonly host!: HTMLElement

  @AttachInternals() private readonly internals!: ElementInternals

  #cleanup?: () => void

  #gap = 0

  #middleware?: Middleware[]

  #referenceElement?: ReferenceElement

  #open = false

  /**
   * Whether the dropdown has been opened at least once.
   * This is used to determine whether the dropdown should update its position, gap
   * and middleware when it is opened.
   */
  #opened = false

  /**
   * Updates the gap between the dropdown and its target element.
   */
  @Method()
  public async updateGap(): Promise<void> {
    this.#gap = propertyToPx(this.host, '--qds-accessory-gap-floating') ?? 0

    await this.updateMiddleware()
  }

  /**
   * Shows the dropdown.
   */
  @Method()
  public async show(): Promise<void> {
    if (this.#open) return

    if (this.#referenceElement instanceof globalThis.Element) {
      this.#referenceElement.setAttribute('aria-expanded', 'true')
      this.#referenceElement.setAttribute('aria-controls', this.host.id)

      if (
        isQdsButton(this.#referenceElement) &&
        this.#referenceElement.action === 'dropdown'
      )
        this.#referenceElement.action = 'dropdown-close'
    }

    this.#open = true
    this.host.hidden = false
    this.host.showPopover()
    this.startAutoUpdate()

    if (!this.#opened) await this.updateGap()
    this.#opened = true
  }

  /**
   * Closes the dropdown.
   */
  @Method()
  public async close(): Promise<void> {
    if (!this.#open) return

    if (this.#referenceElement instanceof globalThis.Element) {
      this.#referenceElement.setAttribute('aria-expanded', 'false')
      this.#referenceElement.removeAttribute('aria-controls')

      if (
        isQdsButton(this.#referenceElement) &&
        this.#referenceElement.action === 'dropdown-close'
      )
        this.#referenceElement.action = 'dropdown'
    }

    this.#open = false
    this.host.hidden = true
    this.host.hidePopover()
    if (this.#cleanup) this.#cleanup()

    this.closeEmitter.emit()
  }

  @Listen('click', { target: 'document' })
  protected async handleMouseDown(event: MouseEvent): Promise<void> {
    if (!this.#open) return

    const composedPath = event.composedPath()
    const withinBounds =
      composedPath.includes(this.host) ||
      (this.#referenceElement instanceof globalThis.Element &&
        composedPath.includes(this.#referenceElement))
    if (!withinBounds) await this.close()
  }

  @Watch('gap')
  protected async onGapChange(): Promise<void> {
    await this.updateMiddleware()
  }

  @Watch('target')
  protected async updateReferenceElement(): Promise<void> {
    this.#disconnectReferenceElement()

    this.#referenceElement = resolveTarget(this.target)

    if (!(this.#referenceElement instanceof globalThis.Element)) return

    this.#referenceElement.setAttribute('aria-expanded', 'false')
    this.#referenceElement.addEventListener(
      'click',
      this.#boundOnReferenceClick,
    )
    this.#referenceElement.addEventListener('focusout', this.#boundOnFocusout)
    this.#referenceElement.addEventListener('keydown', this.#boundOnKeydown)

    if (this.host.role === 'menu') {
      this.#referenceElement.id ||= `qds-dropdown-trigger-${autoIncrementingId}`
      this.host.setAttribute('aria-labelledby', this.#referenceElement.id)
    }
    await this.updatePosition()
  }

  @Watch('flipOptions')
  @Watch('noFlip')
  @Watch('noShift')
  @Watch('offsetOptions')
  @Watch('shiftOptions')
  protected async updateMiddleware(): Promise<void> {
    this.#middleware = [
      offset({
        ...this.offsetOptions,
        mainAxis: this.#gap,
      }),
      !this.noFlip &&
        flip({
          crossAxis: this.noShift,
          ...this.flipOptions,
        }),
      !this.noShift &&
        shift({
          limiter: limitShift(),
          ...this.shiftOptions,
        }),
    ].filter((middleware): middleware is Middleware => middleware !== false)
    await this.updatePosition()
  }

  @Watch('placement')
  @Watch('strategy')
  protected async updatePosition(): Promise<void> {
    if (!this.#referenceElement) return

    const { x, y, strategy } = await computePosition(
      this.#referenceElement,
      this.host,
      {
        middleware: this.#middleware,
        placement: this.placement,
        strategy: this.strategy,
      },
    )
    this.host.style.position = strategy
    this.host.style.transform = `translate(${roundByDPR(x)}px,${roundByDPR(y)}px)`
  }

  @Watch('autoUpdateOptions')
  protected startAutoUpdate(): void {
    if (!this.#referenceElement) return

    if (this.#cleanup) this.#cleanup()
    this.#cleanup = autoUpdate(
      this.#referenceElement,
      this.host,
      () => {
        ignorePromise(this.updatePosition())
      },
      this.autoUpdateOptions,
    )
  }

  public async componentWillLoad(): Promise<void> {
    this.host.hidden = true
    this.host.popover = 'manual'
    this.internals.role = 'menu'
    this.host.id ||= `qds-dropdown-${autoIncrementingId}`
    await this.updateReferenceElement()
    autoIncrementingId += 1
    await this.updateMiddleware()
  }

  public disconnectedCallback(): void {
    this.#disconnectReferenceElement()
  }

  public render() {
    return (
      <Host onKeyDown={this.#onKeydown} onFocusout={this.#onFocusout}>
        <slot />
      </Host>
    )
  }

  readonly #onFocusout = async (event: Event): Promise<void> => {
    if (!(event instanceof FocusEvent)) return
    const { relatedTarget } = event

    if (
      relatedTarget instanceof globalThis.Element &&
      !this.host.contains(relatedTarget) &&
      this.#referenceElement !== relatedTarget
    )
      await this.close()
  }

  readonly #onKeydown = async (event: Event): Promise<void> => {
    if (
      !(event instanceof KeyboardEvent) ||
      event.key !== 'Escape' ||
      (event.target !== this.#referenceElement &&
        event.target instanceof globalThis.Element &&
        event.target.closest('qds-dropdown') !== this.host)
    )
      return

    const cancelEvent = this.cancelEmitter.emit()
    if (cancelEvent.defaultPrevented) return

    await this.close()
    if (
      this.#referenceElement instanceof globalThis.HTMLElement ||
      this.#referenceElement instanceof globalThis.SVGElement
    )
      this.#referenceElement.focus()
  }

  readonly #onReferenceClick = async (): Promise<void> => {
    if (this.disabled) return

    await (this.#open ? this.close() : this.show())
  }

  #disconnectReferenceElement(): void {
    this.host.removeAttribute('aria-labelledby')

    if (!(this.#referenceElement instanceof globalThis.Element)) return

    this.#referenceElement.removeEventListener(
      'focusout',
      this.#boundOnFocusout,
    )
    this.#referenceElement.removeEventListener('keydown', this.#boundOnKeydown)
    this.#referenceElement.removeAttribute('aria-expanded')
    this.#referenceElement.removeAttribute('aria-controls')
  }

  readonly #boundOnFocusout = (event: Event): void => {
    ignorePromise(this.#onFocusout(event))
  }

  readonly #boundOnKeydown = (event: Event): void => {
    ignorePromise(this.#onKeydown(event))
  }

  readonly #boundOnReferenceClick = (): void => {
    ignorePromise(this.#onReferenceClick())
  }
}
